import OfferSections from '../components/OfferSections';
import OfferRevisions from '../components/OfferRevisions';
import OfferAs400Status from '../components/OfferAs400Status';
import { DocumentTypes } from '../components/sales/SalesDocumentViews';
import { clientReturnUrl, money, dateLabel } from '../components/sales/documentTypes';
import OfferPromotionsPanel from '../components/OfferPromotionsPanel';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useClient } from '@offertify/query';
import { offersAPI, templatesAPI, pricingAPI, wmsAPI, promozioniAPI } from '../utils/api';
import { CreateOffertaRequest, CreateOffertaItemRequest, PriceResolution, WmsConnectionStatus, ScontoViolazione, Promozione } from '../types';
import toast from 'react-hot-toast';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon, TrashIcon, DocumentTextIcon, Cog6ToothIcon, ExclamationTriangleIcon, ShieldCheckIcon, CubeIcon, ArrowPathIcon, CalendarDaysIcon, Bars3Icon, PaperAirplaneIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import WmsOrderTracker from '../components/WmsOrderTracker';
import { MarkdownEditorContainer } from '../components/editor';
import { HTMLEditorContainer } from '../components/HTMLEditorContainer';
import ProductAutocomplete, { ProductSelection } from '../components/ProductAutocomplete';
import AttivitaTab from '../components/attivita/AttivitaTab';
import OfferStatusBadge from '../components/OfferStatusBadge';
import ConfirmModal from '../components/ConfirmModal';
import ClientPicker from '../components/ClientPicker';
import { OfferContext } from '../types/editor';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

type TabType = 'details' | 'content' | 'attivita';
type EditorMode = 'markdown' | 'html';

const DEFAULT_MARKDOWN_TEMPLATE = `# {{offerta.titolo}}

## Gentile {{cliente.nome}},

Siamo lieti di presentarLe la nostra proposta commerciale.

{{cliente_info}}

---

## Dettagli dell'Offerta

{{items_table}}

{{totals}}

---

## Condizioni

{{offerta.condizioni_pagamento}}

## Note

{{offerta.note}}

---

{{signature_block}}

Cordiali saluti,
**{{tenant.nome}}**
`;

const OfferEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [routeParams] = useSearchParams();
  const { tenant } = useAuth();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  // Save permission: creating a new offer requires offerte:create,
  // updating an existing one requires offerte:edit.
  const canSave = isEditing ? hasPermission('offerte:edit') : hasPermission('offerte:create');
  const canExportOffer = hasPermission('offerte:export');
  const canSignOffer = hasPermission('offerte:sign');
  const canSendOfferState = hasPermission('offerte:send');
  const canApproveOffer = hasPermission('offerte:approve');
  const canDeleteOffer = hasPermission('offerte:delete');

  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [configurationPinned, setConfigurationPinned] = useState(true);
  const [configurationOpen, setConfigurationOpen] = useState(true);
  const configurationRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (configurationPinned || !configurationOpen) return;
    const closeOutside = (event: MouseEvent) => {
      if (!configurationRef.current?.contains(event.target as Node)) setConfigurationOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, [configurationPinned, configurationOpen]);

  const [editorMode, setEditorMode] = useState<EditorMode>('html'); // Default to HTML editor
  const [formData, setFormData] = useState<CreateOffertaRequest>({
    cliente_id: !id ? routeParams.get('cliente_id') || '' : '',
    titolo: '',
    numero_custom: '',
    descrizione: '',
    markdown_content: DEFAULT_MARKDOWN_TEMPLATE,
    html_content: '',
    importo_totale: 0,
    importo_imponibile: 0,
    importo_iva: 0,
    aliquota_iva: 22,
    scadenza: '',
    giorni_validita: 30,
    note: '',
    condizioni_pagamento: '',
    template_id: '',
    firma_digitale: false,
    items: [],
    promozione_ids: [],
  });

  const savedDocument = useRef<{ form: string; items: string } | null>(null);
  const [items, setItems] = useState<CreateOffertaItemRequest[]>([]);
  // Track pricing resolutions per item index
  const [itemPricing, setItemPricing] = useState<Record<number, PriceResolution>>({});
  // Drag & drop reorder state (dragged row index + drop target row index)
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  // Anteprima violazioni sconto per l'utente corrente (venditore)
  const [scontoViolazioni, setScontoViolazioni] = useState<ScontoViolazione[]>([]);

  // Load offer data if editing
  const { data: offerData, isLoading: isLoadingOffer } = useQuery({
    queryKey: ['offer', id],
    queryFn: () => id ? offersAPI.getOffer(id) : Promise.resolve(null),
    enabled: !!id,
  });

  const { data: selectedClientData } = useClient(formData.cliente_id || undefined);

  // Load templates
  const { data: templatesData } = useQuery({
    queryKey: ['templates'],
    queryFn: () => templatesAPI.getTemplates(),
  });

  // Promozioni attive selezionabili manualmente sull'offerta
  const { data: promozioniAttive, isLoading: promotionsLoading, isError: promotionsError, refetch: refetchPromotions } = useQuery({
    queryKey: ['promozioni-attive'],
    queryFn: async () => {
      const res = await promozioniAPI.getPromozioniAttive();
      return res.data as Promozione[];
    },
  });

  const templates = templatesData?.data?.data || [];
  const selectablePromozioni = [
    ...(promozioniAttive || []),
    ...(offerData?.data?.promozioni || []).filter((promo) =>
      (formData.promozione_ids || []).includes(promo.id) &&
      !(promozioniAttive || []).some((active) => active.id === promo.id)
    ),
  ];

  // WMS integration
  const { data: wmsStatus } = useQuery<WmsConnectionStatus>({
    queryKey: ['wms-status'],
    queryFn: async () => {
      const response = await wmsAPI.getStatus();
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const offer = offerData?.data;
  const returnTo = clientReturnUrl(routeParams.get('returnTo'), offer?.cliente_id || formData.cliente_id) || '/offers';
  const isAccettata = offer?.stato === 'accettata';
  const hasWmsOrder = !!offer?.wms_order_id;

  const createWmsOrder = useMutation({
    mutationFn: (offertaId: string) => wmsAPI.createOrder(offertaId),
    onSuccess: () => {
      toast.success('Ordine magazzino creato');
      if (id) queryClient.invalidateQueries({ queryKey: ['offer', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nella creazione ordine WMS');
    },
  });

  const confirmWmsOrder = useMutation({
    mutationFn: (offertaId: string) => wmsAPI.confirmOrder(offertaId),
    onSuccess: () => {
      toast.success('Ordine confermato');
      if (id) queryClient.invalidateQueries({ queryKey: ['offer', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nella conferma ordine');
    },
  });

  const syncWmsStatus = useMutation({
    mutationFn: (offertaId: string) => wmsAPI.syncOrderStatus(offertaId),
    onSuccess: () => {
      toast.success('Stato aggiornato');
      if (id) queryClient.invalidateQueries({ queryKey: ['offer', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento stato');
    },
  });

  const selectedClient = selectedClientData;

  // Build offer context for the editor
  const offerContext: OfferContext = useMemo(() => {
    return {
      cliente: selectedClient ? {
        nome: selectedClient.nome,
        cognome: selectedClient.cognome,
        ragione_sociale: selectedClient.ragione_sociale,
        email: selectedClient.email,
        telefono: selectedClient.telefono,
        indirizzo: selectedClient.indirizzo ?
          `${selectedClient.indirizzo}, ${selectedClient.cap || ''} ${selectedClient.citta || ''} ${selectedClient.provincia || ''}`.trim() :
          undefined,
        partita_iva: selectedClient.partita_iva,
        codice_fiscale: selectedClient.codice_fiscale,
      } : {
        nome: '',
      },
      offerta: {
        numero: offerData?.data?.numero || 'NUOVO',
        titolo: formData.titolo,
        descrizione: formData.descrizione,
        importo_totale: formData.importo_totale,
        importo_imponibile: formData.importo_imponibile,
        importo_iva: formData.importo_iva,
        aliquota_iva: formData.aliquota_iva,
        scadenza: formData.scadenza,
        giorni_validita: formData.giorni_validita,
        note: formData.note,
        condizioni_pagamento: formData.condizioni_pagamento,
      },
      tenant: tenant ? {
        nome: tenant.name,
        email: tenant.email,
        telefono: tenant.phone,
        indirizzo: `${tenant.address}, ${tenant.postal_code} ${tenant.city} ${tenant.province}`,
        partita_iva: tenant.iva_code,
        website: tenant.website,
        colore_primario: tenant.primary_color,
        colore_secondario: tenant.secondary_color,
      } : {
        nome: '',
      },
    };
  }, [selectedClient, formData, offerData, tenant]);

  // Update form data when offer is loaded
  useEffect(() => {
    if (offerData?.data) {
      const offer = offerData.data;

      // Auto-detect editor mode based on content
      if (offer.html_content) {
        setEditorMode('html');
      } else if (offer.markdown_content) {
        setEditorMode('markdown');
      }

      const loadedForm: CreateOffertaRequest = {
        cliente_id: offer.cliente_id,
        titolo: offer.titolo,
        numero_custom: offer.numero_custom || offer.numero,
        descrizione: offer.descrizione || '',
        markdown_content: offer.markdown_content || DEFAULT_MARKDOWN_TEMPLATE,
        html_content: offer.html_content || '',
        section_values: offer.section_values || {},
        importo_totale: Number(offer.importo_totale),
        importo_imponibile: Number(offer.importo_imponibile),
        importo_iva: Number(offer.importo_iva),
        aliquota_iva: Number(offer.aliquota_iva),
        scadenza: offer.scadenza?.split('T')[0] || '',
        giorni_validita: offer.giorni_validita,
        note: offer.note || '',
        condizioni_pagamento: offer.condizioni_pagamento || '',
        template_id: offer.template_id || '',
        firma_digitale: offer.firma_digitale || false,
        items: [],
        promozione_ids: (offer.promozioni || []).map((p: any) => p.id),
      };
      setFormData(loadedForm);
      savedDocument.current = { form: JSON.stringify(loadedForm), items: '[]' };
      setItems([]);
      // Load items from offer (convert string decimals to numbers)
      if (offer.items && offer.items.length > 0) {
        const loadedItems = offer.items.map((item: any) => ({
          descrizione: item.descrizione,
          quantita: Number(item.quantita),
          unita_misura: item.unita_misura || '',
          prezzo_unitario: Number(item.prezzo_unitario),
          sconto_percentuale: Number(item.sconto_percentuale || 0),
          aliquota_iva: item.aliquota_iva != null ? Number(item.aliquota_iva) : Number(offer.aliquota_iva),
          importo_totale: Number(item.importo_totale),
          ordine: item.ordine,
          prodotto_id: item.prodotto_id || undefined,
        }));
        setItems(loadedItems);
        savedDocument.current.items = JSON.stringify(loadedItems);

        // Restore pricing info from saved promozioni_applicate
        const savedPricing: Record<number, PriceResolution> = {};
        offer.items.forEach((item: any, idx: number) => {
          if (item.promozioni_applicate && item.promozioni_applicate.length > 0) {
            savedPricing[idx] = {
              prodotto_id: item.prodotto_id || '',
              prodotto_nome: item.descrizione,
              prezzo_base: Number(item.prezzo_unitario),
              prezzo_listino: item.prezzo_listino != null ? Number(item.prezzo_listino) : undefined,
              prezzo_finale: item.prezzo_promozione != null ? Number(item.prezzo_promozione) : Number(item.prezzo_unitario),
              promozioni_applicate: item.promozioni_applicate.map((p: any) => ({
                promozione_id: p.promozione_id,
                nome: p.nome,
                tipo: p.tipo,
                valore: Number(p.valore),
                prezzo_prima: Number(p.prezzo_prima),
                prezzo_dopo: Number(p.prezzo_dopo),
                impatta_prezzo: p.impatta_prezzo !== false,
              })),
            };
          } else if (item.prezzo_listino != null) {
            savedPricing[idx] = {
              prodotto_id: item.prodotto_id || '',
              prodotto_nome: item.descrizione,
              prezzo_base: Number(item.prezzo_unitario),
              prezzo_listino: Number(item.prezzo_listino),
              prezzo_finale: item.prezzo_promozione != null ? Number(item.prezzo_promozione) : Number(item.prezzo_unitario),
              promozioni_applicate: [],
            };
          }
        });
        if (Object.keys(savedPricing).length > 0) {
          setItemPricing(savedPricing);
        }
      }
    }
  }, [offerData]);

  // Re-resolve pricing when client changes
  useEffect(() => {
    if (!formData.cliente_id || items.length === 0) return;

    const prodottoIds = items
      .map(item => item.prodotto_id)
      .filter((id): id is string => !!id);

    if (prodottoIds.length === 0) return;

    const resolvePrices = async () => {
      try {
        const response = await pricingAPI.resolvePricesBatch(formData.cliente_id, prodottoIds);
        const resolutions = response.data.prezzi;

        const newPricing: Record<number, PriceResolution> = {};
        const updatedItems = [...items];
        let changed = false;

        items.forEach((item, idx) => {
          if (!item.prodotto_id) return;
          const resolution = resolutions.find(r => r.prodotto_id === item.prodotto_id);
          if (resolution) {
            newPricing[idx] = resolution;
            // Update price if it was from a previous pricing resolution (not manually set)
            const prevPricing = itemPricing[idx];
            if (prevPricing && Number(item.prezzo_unitario) === Number(prevPricing.prezzo_finale)) {
              updatedItems[idx] = { ...updatedItems[idx], prezzo_unitario: Number(resolution.prezzo_finale) };
              const subtotal = updatedItems[idx].quantita * Number(resolution.prezzo_finale);
              const discount = subtotal * ((updatedItems[idx].sconto_percentuale || 0) / 100);
              updatedItems[idx].importo_totale = subtotal - discount;
              changed = true;
            }
          }
        });

        setItemPricing(newPricing);
        if (changed) {
          setItems(updatedItems);
          calculateTotals(updatedItems);
        }
      } catch {
        // Pricing resolution is best-effort
      }
    };

    resolvePrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.cliente_id]);

  // Anteprima live dei limiti sconto (venditore): avvisa prima del salvataggio
  // che sara' richiesta l'approvazione
  useEffect(() => {
    const hasSconti = items.some(it => (it.sconto_percentuale || 0) > 0);
    if (!hasSconti) {
      setScontoViolazioni([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await offersAPI.validateSconti({
          items: items.map(it => ({
            descrizione: it.descrizione,
            sconto_percentuale: it.sconto_percentuale || 0,
            prodotto_id: it.prodotto_id,
          })),
        });
        setScontoViolazioni(res.data.violazioni || []);
      } catch {
        // Anteprima best-effort: il controllo vero avviene al salvataggio
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [items, formData.cliente_id]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateOffertaRequest) => offersAPI.createOffer(data),
    onSuccess: (response) => {
      toast.success('Offerta creata con successo!');
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      queryClient.invalidateQueries({ queryKey: ['client-offers'] });
      const newId = response.data?.id;
      // Stay on the offer, switching to edit mode
      if (newId) {
        navigate(`/offers/${newId}/edit${returnTo !== '/offers' ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`, { replace: true });
      } else {
        navigate(returnTo);
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Errore durante la creazione');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: CreateOffertaRequest) => offersAPI.updateOffer(id!, data),
    onSuccess: () => {
      toast.success('Offerta aggiornata con successo!');
      // Stay in the editor instead of going back to the list
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      queryClient.invalidateQueries({ queryKey: ['client-offers'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Errore durante l\'aggiornamento');
    },
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const hasUnsavedChanges = !!savedDocument.current && (
    savedDocument.current.form !== JSON.stringify(formData) || savedDocument.current.items !== JSON.stringify(items)
  );

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Shared shape for the three state-transition mutations below: call the API,
  // toast, and refresh the offer on success; toast the backend's error detail
  // (or a fallback) on failure.
  const useOfferTransitionMutation = <TVars,>(
    mutationFn: (vars: TVars) => Promise<any>,
    successMessage: string,
    errorFallback: string,
    onSuccessExtra?: () => void,
  ) => useMutation({
    mutationFn,
    onSuccess: () => {
      toast.success(successMessage);
      onSuccessExtra?.();
      queryClient.invalidateQueries({ queryKey: ['offer', id] });
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      queryClient.invalidateQueries({ queryKey: ['client-offers'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || errorFallback);
    },
  });

  const requestValidationMutation = useOfferTransitionMutation<void>(
    () => offersAPI.requestValidation(id!), 'Validazione richiesta', 'Impossibile richiedere la validazione'
  );
  const validateOfferMutation = useOfferTransitionMutation<void>(
    () => offersAPI.validateOffer(id!), 'Offerta validata', 'Impossibile validare l’offerta'
  );
  const approveDiscountMutation = useOfferTransitionMutation<void>(
    () => offersAPI.approveDiscount(id!), 'Sconti approvati', 'Impossibile approvare gli sconti'
  );
  const sendOfferMutation = useOfferTransitionMutation<void>(
    () => offersAPI.sendOffer(id!), 'Offerta inviata', 'Errore durante l\'invio dell\'offerta'
  );
  const acceptOfferMutation = useOfferTransitionMutation<void>(
    () => offersAPI.acceptOffer(id!), 'Offerta accettata', 'Errore durante l\'accettazione dell\'offerta'
  );
  const rejectOfferMutation = useOfferTransitionMutation<string>(
    (note) => offersAPI.rejectOffer(id!, note || undefined),
    'Offerta rifiutata',
    'Errore durante il rifiuto dell\'offerta',
    () => { setShowRejectModal(false); setRejectNote(''); }
  );

  const deleteOfferMutation = useMutation({
    mutationFn: () => offersAPI.deleteOffer(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      queryClient.invalidateQueries({ queryKey: ['client-offers'] });
      toast.success('Offerta eliminata');
      navigate(returnTo);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore durante l'eliminazione");
    },
  });

  // Shared submit logic: used by the form and by the floating totals bar
  // (which lives outside the <form>, so browser validation is re-done manually)
  const submitOffer = () => {
    if (!formData.cliente_id) {
      toast.error('Seleziona un cliente');
      setActiveTab('details');
      return;
    }
    if (!formData.titolo.trim()) {
      toast.error('Inserisci il titolo dell\'offerta');
      setActiveTab('details');
      return;
    }

    const data = {
      ...formData,
      numero_custom: formData.numero_custom?.trim() || null,
      items: items,
    };

    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitOffer();
  };

  // Flag set when a new item is added: the row would end up below the fold,
  // hidden behind the floating totals bar, so we scroll it into view
  const scrollToNewItem = useRef(false);

  useEffect(() => {
    if (!scrollToNewItem.current) return;
    scrollToNewItem.current = false;
    const rows = document.querySelectorAll('[data-offer-item-row]');
    const lastRow = rows[rows.length - 1] as HTMLElement | undefined;
    if (lastRow) {
      lastRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      lastRow.querySelector('input')?.focus();
    }
  }, [items]);

  const addItem = () => {
    const newItem: CreateOffertaItemRequest = {
      descrizione: '',
      quantita: 1,
      unita_misura: '',
      prezzo_unitario: 0,
      sconto_percentuale: 0,
      aliquota_iva: formData.aliquota_iva,
      importo_totale: 0,
      ordine: items.length,
    };
    scrollToNewItem.current = true;
    setItems([...items, newItem]);
  };

  const updateItem = (index: number, field: keyof CreateOffertaItemRequest, value: any) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };

    // Calculate total for this item
    const item = updatedItems[index];
    const subtotal = item.quantita * item.prezzo_unitario;
    const discount = subtotal * ((item.sconto_percentuale || 0) / 100);
    item.importo_totale = subtotal - discount;

    setItems(updatedItems);
    calculateTotals(updatedItems);
  };

  const removeItem = (index: number) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
    calculateTotals(updatedItems);
  };

  // Move an item from one position to another (drag & drop reorder)
  const moveItem = (from: number, to: number) => {
    if (from === to || from < 0 || from >= items.length || to < 0 || to >= items.length) return;

    const updatedItems = [...items];
    const [moved] = updatedItems.splice(from, 1);
    updatedItems.splice(to, 0, moved);
    // Keep ordine aligned with the new positions
    setItems(updatedItems.map((item, idx) => ({ ...item, ordine: idx })));

    // Pricing info is keyed by row index: remap it so it follows the rows
    const pricingArr = items.map((_, idx) => itemPricing[idx]);
    const [movedPricing] = pricingArr.splice(from, 1);
    pricingArr.splice(to, 0, movedPricing);
    const newPricing: Record<number, PriceResolution> = {};
    pricingArr.forEach((p, idx) => {
      if (p) newPricing[idx] = p;
    });
    setItemPricing(newPricing);
  };

  const resetDrag = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  // IVA calcolata per singolo articolo: ogni riga usa la propria aliquota,
  // aliquotaIva e' il default per le righe che non ce l'hanno
  const calculateTotals = (items: CreateOffertaItemRequest[], aliquotaIva: number = formData.aliquota_iva) => {
    const imponibile = items.reduce((sum, item) => sum + Number(item.importo_totale || 0), 0);
    const iva = items.reduce((sum, item) => {
      const rate = item.aliquota_iva ?? aliquotaIva;
      return sum + Number(item.importo_totale || 0) * (rate / 100);
    }, 0);
    const totale = imponibile + iva;

    setFormData(prev => ({
      ...prev,
      importo_imponibile: imponibile,
      importo_iva: iva,
      importo_totale: totale,
    }));
  };

  // Handle markdown content save from editor
  const handleMarkdownSave = async (content: string) => {
    setFormData(prev => ({ ...prev, markdown_content: content }));
    // If editing, also save to server
    if (isEditing && id) {
      try {
        await offersAPI.updateOffer(id, {
          ...formData,
          markdown_content: content,
          items: items,
        });
        toast.success('Contenuto salvato');
      } catch (error) {
        toast.error('Errore durante il salvataggio');
        throw error;
      }
    }
  };

  // Handle HTML content save from editor
  const handleHTMLSave = async (content: string) => {
    setFormData(prev => ({ ...prev, html_content: content }));
    // If editing, also save to server
    if (isEditing && id) {
      try {
        await offersAPI.updateOffer(id, {
          ...formData,
          html_content: content,
          items: items,
        });
        toast.success('Contenuto salvato');
      } catch (error) {
        toast.error('Errore durante il salvataggio');
        throw error;
      }
    }
  };

  // Handle PDF export
  const handleExportPDF = async () => {
    if (!id) {
      toast.error('Salva prima l\'offerta per esportare il PDF');
      return;
    }
    try {
      toast.loading('Generazione PDF in corso...', { id: 'pdf-generation' });

      // First generate a new PDF version
      const generateResponse = await offersAPI.generatePDF(id, true);

      if (generateResponse.data.success) {
        // Now download the PDF
        const downloadResponse = await offersAPI.downloadPDF(id, false);

        // Create a blob URL and trigger download
        const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `offerta_${offerData?.data?.numero?.replace('/', '-') || id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast.success('PDF scaricato con successo', { id: 'pdf-generation' });
      } else {
        toast.error('Errore durante la generazione del PDF', { id: 'pdf-generation' });
      }
    } catch (error: any) {
      console.error('PDF export error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante la generazione del PDF', { id: 'pdf-generation' });
    }
  };

  // Handle PDF preview (opens in new tab)
  const handlePreviewPDF = async () => {
    if (!id) {
      toast.error('Salva prima l\'offerta per visualizzare l\'anteprima');
      return;
    }
    try {
      toast.loading('Generazione anteprima...', { id: 'pdf-preview' });

      const response = await offersAPI.downloadPDF(id, true);

      // Create a blob URL and open in new tab
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');

      toast.dismiss('pdf-preview');
    } catch (error: any) {
      console.error('PDF preview error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante la generazione dell\'anteprima', { id: 'pdf-preview' });
    }
  };

  // Handle send for signature
  const handleSendForSignature = async () => {
    if (!id) {
      toast.error('Salva prima l\'offerta per inviarla per la firma');
      return;
    }
    try {
      await offersAPI.sendForSignature(id);
      toast.success('Offerta inviata per la firma');
    } catch (error) {
      toast.error('Errore durante l\'invio per la firma');
    }
  };

  if (isLoadingOffer) {
    return <div className="p-8 text-center">Caricamento...</div>;
  }

  return (
    <div className="space-y-4">
      <DocumentTypes current="offerta" />
      {offer && <OfferRevisions id={offer.id} canRevise={offer.stato === 'inviata' && hasPermission('offerte:edit')} />}
      <button type="button" onClick={() => navigate(returnTo)} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900"><ChevronLeftIcon className="h-4 w-4" />Torna all’elenco</button>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
            {isEditing ? `Offerta ${offer?.numero || ''}` : 'Nuova offerta'}
          </h1>
          {isEditing && offer && <p className="mt-2 text-sm text-gray-600">{dateLabel(offer.created_at)}{offer.scadenza && ` · Scadenza ${dateLabel(offer.scadenza)}`}</p>}
          <p className="mt-2 text-xs text-gray-500">Totale documento <span className="ml-2 text-base font-semibold tabular-nums text-gray-900">{money(String(formData.importo_totale), 'EUR')}</span></p>
        </div>
        <div className="flex min-w-0 flex-1 flex-wrap items-start justify-end gap-2">
          <div className="min-w-0 flex-1">
      {/* Offer Status + Transitions */}
      {isEditing && offer && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <OfferStatusBadge stato={offer.stato} commerciale={offer.stato_commerciale} />
          <OfferAs400Status offerId={offer.id} />
          {offer.stato === 'accettata' && hasPermission('offerte:create') && <button type="button" className="rounded border border-blue-300 px-3 py-1.5 text-sm text-blue-700" disabled={isSaving || hasUnsavedChanges} onClick={() => navigate(`/sales-documents?offerta=${offer.id}${returnTo !== '/offers' ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`)}>Ordini e documenti</button>}

          {offer.stato === 'bozza' && <>
            {hasPermission('offerte:edit') && !offer.validazione_stato && <button type="button" onClick={() => requestValidationMutation.mutate()} disabled={requestValidationMutation.isPending || isSaving || hasUnsavedChanges} className="rounded border border-blue-300 px-3 py-1.5 text-sm text-blue-700">Richiedi validazione</button>}
            {offer.approvazione_richiesta && offer.approvazione_stato === 'pending' && hasPermission('offerte:approve_discount') && <button type="button" onClick={() => approveDiscountMutation.mutate()} disabled={approveDiscountMutation.isPending || isSaving || hasUnsavedChanges} className="rounded border border-green-300 px-3 py-1.5 text-sm text-green-700">Approva sconti</button>}
            {offer.validazione_stato === 'pending' && canApproveOffer && <button type="button" onClick={() => validateOfferMutation.mutate()} disabled={validateOfferMutation.isPending || isSaving || hasUnsavedChanges || (offer.approvazione_richiesta && offer.approvazione_stato !== 'approved')} className="rounded border border-green-300 px-3 py-1.5 text-sm text-green-700">Valida offerta</button>}
          </>}
          {offer.stato === 'bozza' && canSendOfferState && (
            <button
              type="button"
              onClick={() => sendOfferMutation.mutate()}
              disabled={sendOfferMutation.isPending || isSaving || hasUnsavedChanges || offer.validazione_stato !== 'approved' || (offer.approvazione_richiesta && offer.approvazione_stato !== 'approved')}
              title={offer.approvazione_richiesta && offer.approvazione_stato !== 'approved' ? 'Richiede approvazione sconti prima dell\'invio' : undefined}
              className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PaperAirplaneIcon className="h-4 w-4 mr-1" />
              {sendOfferMutation.isPending ? 'Invio...' : 'Invia offerta'}
            </button>
          )}

          {offer.stato === 'bozza' && canDeleteOffer && (
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
            >
              <TrashIcon className="h-4 w-4 mr-1" />
              Elimina
            </button>
          )}

          {offer.stato === 'inviata' && canApproveOffer && (
            <>
              <button
                type="button"
                onClick={() => acceptOfferMutation.mutate()}
                disabled={acceptOfferMutation.isPending}
                className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                <CheckCircleIcon className="h-4 w-4 mr-1" />
                {acceptOfferMutation.isPending ? 'Accettazione...' : 'Accetta'}
              </button>
              <button
                type="button"
                onClick={() => setShowRejectModal(true)}
                className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
              >
                <XCircleIcon className="h-4 w-4 mr-1" />
                Rifiuta
              </button>
            </>
          )}
        </div>
      )}

          </div>
        <button
          onClick={() => navigate(returnTo)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Annulla
        </button>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-4 max-w-md">
          <label htmlFor="offer-number" className="mb-1 block text-sm font-medium text-gray-700">Numero offerta</label>
          <input id="offer-number" type="text" maxLength={50}
            value={formData.numero_custom || ''}
            onChange={event => setFormData(previous => ({ ...previous, numero_custom: event.target.value }))}
            disabled={isEditing && offer?.stato !== 'bozza'}
            placeholder="Automatico, se non ancora assegnato"
            aria-describedby="offer-number-help"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100" />
          <p id="offer-number-help" className="mt-1 text-xs text-gray-500">Se l’offerta ha già un numero, inseriscilo esattamente come nel documento condiviso. Lascia vuoto solo per una nuova numerazione progressiva.</p>
        </div>
        <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="offer-client" className="block text-sm font-medium text-gray-700 mb-1">
                  Cliente *
                </label>
                <ClientPicker
                  value={formData.cliente_id}
                  selectedClient={selectedClient}
                  onChange={(client) => setFormData({ ...formData, cliente_id: client.id })}
                  disabled={isEditing && offer?.stato !== 'bozza'}
                />
                {isEditing && offer?.stato !== 'bozza' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Il cliente non può essere modificato dopo l'invio dell'offerta.
                  </p>
                )}
              </div>
            <div className="min-w-0">
              <label htmlFor="offer-title" className="block text-sm font-medium text-gray-700 mb-1">
                Titolo *
              </label>
              <input
                id="offer-title"
                type="text"
                value={formData.titolo}
                onChange={(e) => setFormData({ ...formData, titolo: e.target.value })}
                required
                className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Inserisci il titolo dell'offerta"
              />
            </div>
        </div>
        <div className="mt-3 empty:hidden">
              {/* Riepilogo del cliente selezionato */}
              {selectedClient && (
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                  <span className="font-medium text-gray-900">
                    {selectedClient.ragione_sociale || `${selectedClient.nome} ${selectedClient.cognome || ''}`.trim()}
                  </span>
                  {selectedClient.partita_iva && <span>P.IVA {selectedClient.partita_iva}</span>}
                  {selectedClient.codice_fiscale && <span>CF {selectedClient.codice_fiscale}</span>}
                  {selectedClient.email && <span>{selectedClient.email}</span>}
                  {selectedClient.telefono && <span>Tel. {selectedClient.telefono}</span>}
                  {(selectedClient.indirizzo || selectedClient.citta) && (
                    <span>
                      {[selectedClient.indirizzo, selectedClient.cap, selectedClient.citta, selectedClient.provincia]
                        .filter(Boolean)
                        .join(' ')}
                    </span>
                  )}
                </div>
              )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="-mb-px flex space-x-6">
          <button
            onClick={() => setActiveTab('details')}
            className={`
              flex items-center py-2.5 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'details'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <Cog6ToothIcon className="h-4 w-4 mr-1.5" />
            Dettagli Offerta
          </button>
          <button
            onClick={() => setActiveTab('content')}
            className={`
              flex items-center py-2.5 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'content'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <DocumentTextIcon className="h-4 w-4 mr-1.5" />
            Contenuto Documento
          </button>
          <button
            onClick={() => setActiveTab('attivita')}
            className={`
              flex items-center py-2.5 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'attivita'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <CalendarDaysIcon className="h-4 w-4 mr-1.5" />
            Attività
          </button>
        </nav>
      </div>

      {/* Approval Warning Banner */}
      {isEditing && offerData?.data?.approvazione_richiesta && (
        <div className={`rounded-md p-3 mb-4 ${
          offerData.data.approvazione_stato === 'pending'
            ? 'bg-yellow-50 border border-yellow-200'
            : offerData.data.approvazione_stato === 'approved'
            ? 'bg-green-50 border border-green-200'
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {offerData.data.approvazione_stato === 'pending' && (
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
              )}
              {offerData.data.approvazione_stato === 'approved' && (
                <ShieldCheckIcon className="h-5 w-5 text-green-400" />
              )}
              {offerData.data.approvazione_stato === 'rejected' && (
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              )}
            </div>
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${
                offerData.data.approvazione_stato === 'pending'
                  ? 'text-yellow-800'
                  : offerData.data.approvazione_stato === 'approved'
                  ? 'text-green-800'
                  : 'text-red-800'
              }`}>
                {offerData.data.approvazione_stato === 'pending' && 'In attesa di approvazione sconti'}
                {offerData.data.approvazione_stato === 'approved' && 'Sconti approvati'}
                {offerData.data.approvazione_stato === 'rejected' && 'Sconti rifiutati'}
              </h3>
              <div className={`mt-1 text-sm ${
                offerData.data.approvazione_stato === 'pending'
                  ? 'text-yellow-700'
                  : offerData.data.approvazione_stato === 'approved'
                  ? 'text-green-700'
                  : 'text-red-700'
              }`}>
                {offerData.data.approvazione_stato === 'pending' && (
                  <p>Questa offerta contiene sconti che superano il limite autorizzato. Non può essere inviata fino all'approvazione.</p>
                )}
                {offerData.data.approvazione_stato === 'approved' && (
                  <p>Gli sconti applicati sono stati approvati. L'offerta può essere inviata.</p>
                )}
                {offerData.data.approvazione_stato === 'rejected' && (
                  <>
                    <p>Gli sconti applicati sono stati rifiutati. Modifica gli sconti e riprova.</p>
                    {offerData.data.approvazione_note && (
                      <p className="mt-1 font-medium">Motivo: {offerData.data.approvazione_note}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WMS Order Section */}
      {isEditing && wmsStatus?.connected && isAccettata && (
        <div className="bg-white shadow rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <CubeIcon className="h-5 w-5 text-gray-400" />
            <h3 className="text-sm font-medium text-gray-900">Ordine Magazzino</h3>
          </div>

          {!hasWmsOrder && (
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-500">Nessun ordine magazzino associato.</p>
              <button
                onClick={() => id && createWmsOrder.mutate(id)}
                disabled={createWmsOrder.isPending}
                className="px-3 py-1.5 text-xs border border-blue-300 rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {createWmsOrder.isPending ? 'Creazione...' : 'Crea Ordine Magazzino'}
              </button>
            </div>
          )}

          {hasWmsOrder && offer && (
            <div className="space-y-3">
              <WmsOrderTracker
                wmsOrderStatus={offer.wms_order_status || 'DRAFT'}
                statoEvasione={offer.stato_evasione || 'non_evaso'}
                wmsOrderNumber={offer.wms_order_number || ''}
              />
              <div className="flex gap-2 pt-2">
                {offer.wms_order_status === 'DRAFT' && (
                  <button
                    onClick={() => id && confirmWmsOrder.mutate(id)}
                    disabled={confirmWmsOrder.isPending}
                    className="px-3 py-1.5 text-xs border border-green-300 rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                  >
                    {confirmWmsOrder.isPending ? 'Conferma...' : 'Conferma Ordine'}
                  </button>
                )}
                <button
                  onClick={() => id && syncWmsStatus.mutate(id)}
                  disabled={syncWmsStatus.isPending}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <ArrowPathIcon className={`h-3 w-3 ${syncWmsStatus.isPending ? 'animate-spin' : ''}`} />
                  Aggiorna Stato
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Details Tab */}
      {activeTab === 'details' && (
        <form onSubmit={handleSubmit} className={`grid min-w-0 grid-cols-1 items-start gap-4 ${configurationOpen ? 'lg:grid-cols-[minmax(0,1fr)_22rem]' : 'lg:grid-cols-[minmax(0,1fr)_2.5rem]'}`}>
          <div className="order-2 min-w-0 space-y-4 lg:order-1">
          {/* Items */}
          <div className="bg-white shadow rounded-lg p-4">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-base font-medium text-gray-900">Articoli</h2>
              <button
                type="button"
                onClick={addItem}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm leading-4 font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <PlusIcon className="h-4 w-4 mr-1.5" />
                Aggiungi Articolo
              </button>
            </div>

            {/* Avviso preventivo: sconti oltre il limite dell'utente */}
            {scontoViolazioni.length > 0 && (
              <div className="mb-3 rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 flex items-start gap-2">
                <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-yellow-800">
                  {scontoViolazioni.length === 1
                    ? 'Una riga supera il tuo limite di sconto'
                    : `${scontoViolazioni.length} righe superano il tuo limite di sconto`}
                  : al salvataggio l'offerta richiederà l'approvazione di un responsabile prima di poter essere inviata.
                </p>
              </div>
            )}

            {items.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <PlusIcon className="mx-auto h-10 w-10 text-gray-400 mb-3" />
                <p className="text-sm">Nessun articolo aggiunto</p>
                <button
                  type="button"
                  onClick={addItem}
                  className="mt-2 text-primary-600 hover:text-primary-700"
                >
                  Aggiungi il primo articolo
                </button>
              </div>
            ) : (
              <div role="region" aria-label="Elenco articoli" tabIndex={0} className="max-h-[60dvh] overflow-auto overscroll-contain">
                {/* Table rows */}
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                  {items.map((item, index) => {
                    const pricing = itemPricing[index];
                    const hasPromos = pricing?.promozioni_applicate && pricing.promozioni_applicate.length > 0;
                    const listino = pricing ? Number(pricing.prezzo_listino ?? pricing.prezzo_base) : null;
                    const violazione = scontoViolazioni.find(v => v.item_index === index);

                    return (
                      <div
                        key={index}
                        data-offer-item-row
                        onDragOver={(e) => {
                          if (dragIndex === null || dragIndex === index) return;
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                          setDragOverIndex(index);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (dragIndex !== null) moveItem(dragIndex, index);
                          resetDrag();
                        }}
                        className={`${hasPromos ? 'bg-green-50/30' : ''} ${dragIndex === index ? 'opacity-40' : ''} ${
                          dragOverIndex === index && dragIndex !== null && dragIndex !== index
                            ? 'shadow-[inset_0_2px_0_0_#2563eb]'
                            : ''
                        }`}
                      >
                        <div className="px-3 py-3">
                          <div className="flex items-center gap-2">
                          {/* Handle drag & drop */}
                          <div className="flex shrink-0 justify-center">
                            <button
                              type="button"
                              draggable
                              onDragStart={(e) => {
                                setDragIndex(index);
                                e.dataTransfer.effectAllowed = 'move';
                                e.dataTransfer.setData('text/plain', String(index));
                                const row = (e.currentTarget as HTMLElement).closest('[data-offer-item-row]');
                                if (row) e.dataTransfer.setDragImage(row as HTMLElement, 16, 16);
                              }}
                              onDragEnd={resetDrag}
                              className="p-2 text-gray-400 hover:text-gray-500 cursor-grab active:cursor-grabbing select-none"
                              title="Trascina per riordinare"
                            >
                              <Bars3Icon className="h-4 w-4" />
                            </button>
                          </div>

                          {/* Descrizione */}
                          <div className="min-w-0 flex-1 [&_input]:font-medium">
                            <span className="sr-only">Descrizione articolo {index + 1}</span>
                            <ProductAutocomplete
                              value={item.descrizione}
                              onChange={(val) => updateItem(index, 'descrizione', val)}
                              clienteId={formData.cliente_id || undefined}
                              onProductSelect={(sel: ProductSelection) => {
                                const updated = [...items];
                                updated[index] = {
                                  ...updated[index],
                                  descrizione: sel.descrizione,
                                  prezzo_unitario: sel.prezzo_unitario,
                                  unita_misura: sel.unita_misura,
                                  aliquota_iva: sel.aliquota_iva ?? updated[index].aliquota_iva ?? formData.aliquota_iva,
                                  prodotto_id: sel.prodotto_id,
                                };
                                const subtotal = updated[index].quantita * sel.prezzo_unitario;
                                const discount = subtotal * ((updated[index].sconto_percentuale || 0) / 100);
                                updated[index].importo_totale = subtotal - discount;
                                setItems(updated);
                                calculateTotals(updated);
                                if (sel.pricing) {
                                  setItemPricing(prev => ({ ...prev, [index]: sel.pricing! }));
                                }
                              }}
                              required
                            />
                          </div>

                          {/* Elimina */}
                          <div className="flex shrink-0 justify-center">
                            <button
                              type="button"
                              aria-label={`Rimuovi articolo ${index + 1}`}
                              title="Rimuovi articolo"
                              onClick={() => removeItem(index)}
                              className="p-2 text-gray-400 hover:bg-red-50 rounded hover:text-red-600 transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                          </div>
                          {pricing?.prodotto_nome && pricing.prodotto_nome !== item.descrizione && (
                            <p className="ml-10 mt-1 text-xs text-gray-500">{pricing.prodotto_nome}</p>
                          )}
                          <div className="mt-3 grid grid-cols-2 items-end gap-3 sm:grid-cols-5 tabular-nums">
                          {/* Quantità */}
                          <div>
                            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-500">Quantità</span>
                            <div className="relative">
                            <input
                              type="number"
                              min="1"
                              step="0.01"
                              aria-label={`Quantità articolo ${index + 1}`}
                              value={item.quantita}
                              onChange={(e) => updateItem(index, 'quantita', parseFloat(e.target.value) || 0)}
                              required
                              className="block w-full pl-2 pr-9 py-1.5 text-sm text-right border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                            />
                            <span className="pointer-events-none absolute right-2 top-1.5 text-sm text-gray-500">{item.unita_misura || '—'}</span>
                            </div>
                          </div>

                          {/* Prezzo Unitario (read-only, shows promo price) */}
                          <div className="text-right">
                            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-500">Prezzo unit.</span>
                            <span className={`flex h-[34px] items-center justify-end text-sm font-medium ${hasPromos ? 'text-green-700' : 'text-gray-900'}`}>
                              €{Number(item.prezzo_unitario).toFixed(2)}
                            </span>
                          </div>

                          {/* Sconto % */}
                          <div>
                            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-500">Sconto %</span>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              aria-label={`Sconto articolo ${index + 1}`}
                              value={item.sconto_percentuale}
                              onChange={(e) => updateItem(index, 'sconto_percentuale', parseFloat(e.target.value) || 0)}
                              className={`block w-full px-2 py-1.5 text-sm text-right border rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                                violazione || (isEditing && offerData?.data?.approvazione_richiesta && offerData?.data?.approvazione_stato === 'pending' && (item.sconto_percentuale || 0) > 0)
                                  ? 'border-yellow-400 bg-yellow-50'
                                  : 'border-gray-300'
                              }`}
                            />
                          </div>

                          <div className="text-right">
                            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-500">Netto unit.</span>
                            <span className="flex h-[34px] items-center justify-end text-sm text-gray-900">€{(Number(item.prezzo_unitario) * (1 - Number(item.sconto_percentuale || 0) / 100)).toFixed(2)}</span>
                          </div>
                          {/* Totale riga */}
                          <div className="text-right font-semibold text-base text-gray-900">
                            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-500">Totale riga</span>
                            <span className="flex h-[34px] items-center justify-end">€{Number(item.importo_totale || 0).toFixed(2)}</span>
                          </div>

                          </div>
                          <div className="mt-2 flex flex-wrap justify-end gap-x-4 gap-y-1 text-[11px] text-gray-500">
                            {listino != null && <span>Listino €{listino.toFixed(2)}</span>}
                            <span>IVA {Number(item.aliquota_iva ?? formData.aliquota_iva)}% esclusa</span>
                          </div>
                        </div>

                        {/* Sub-row: promozioni + avviso limite sconto */}
                        {(hasPromos || violazione) && (
                          <div className="px-3 pb-2 flex items-center gap-1.5 flex-wrap">
                            {hasPromos && pricing!.promozioni_applicate.map((p) => (
                              <span
                                key={p.promozione_id}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${
                                  p.impatta_prezzo === false
                                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                                    : 'bg-green-100 text-green-700 border-green-200'
                                }`}
                              >
                                {p.nome}
                                {p.impatta_prezzo === false ? (
                                  <span className="font-medium">Solo tracciamento</span>
                                ) : (
                                  <>
                                    <span className="font-semibold">
                                      {p.tipo === 'sconto_percentuale' ? `-${Number(p.valore)}%` : `€${Number(p.valore).toFixed(2)}`}
                                    </span>
                                    <span className="text-green-500">
                                      €{Number(p.prezzo_prima).toFixed(2)} → €{Number(p.prezzo_dopo).toFixed(2)}
                                    </span>
                                  </>
                                )}
                              </span>
                            ))}
                            {violazione && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800 border border-yellow-200">
                                Sconto {Number(violazione.sconto_richiesto)}% oltre il tuo limite (max {Number(violazione.sconto_max)}%): richiede approvazione
                              </span>
                            )}
                            {!violazione && isEditing && offerData?.data?.approvazione_richiesta && offerData?.data?.approvazione_stato !== 'approved' && (item.sconto_percentuale || 0) > 0 && (
                              <span className="text-xs text-yellow-600 ml-1">Sconto oltre il limite</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          </div>
          <div ref={configurationRef} aria-label="Configurazione offerta" className="order-1 flex min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-white lg:order-2 lg:sticky lg:top-4"
            onKeyDown={(event) => {
              if (event.key === 'Escape' && !configurationPinned) setConfigurationOpen(false);
            }}>
            <div hidden={!configurationOpen} className="min-w-0 flex-1">
              <div className="flex h-12 items-center justify-between border-b border-gray-200 bg-gray-50 px-4">
                <h2 className="text-sm font-semibold text-gray-900">Configurazione offerta</h2>
                <button type="button" aria-pressed={configurationPinned}
                  aria-label={configurationPinned ? 'Sblocca pannello (unpin)' : 'Fissa pannello (pin)'}
                  title={configurationPinned ? 'Sblocca: si chiude cliccando fuori' : 'Fissa: mantieni aperto'}
                  onClick={() => setConfigurationPinned(!configurationPinned)}
                  className={`rounded p-2 hover:bg-gray-200 ${configurationPinned ? 'text-primary-700' : 'text-gray-500'}`}>
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill={configurationPinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 3h6l-1 6 4 4v2h-5v6l-1 1-1-1v-6H6v-2l4-4-1-6Z" />
                  </svg>
                </button>
              </div>
            <div id="offer-configuration-panel" className="max-h-[calc(100dvh-12rem)] overflow-y-auto divide-y divide-gray-200 [&>div]:rounded-none [&>div]:border-0 [&>aside]:rounded-none [&>aside]:border-0">
          {/* Client and Basic Info */}
          <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="text-base font-medium text-gray-900 mb-3">Informazioni Generali</h2>

            <div className="grid grid-cols-1 gap-3">


              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template
                </label>
                <select
                  value={formData.template_id}
                  onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                  className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Nessun template</option>
                  {templates.map((template: any) => (
                    <option key={template.id} value={template.id}>
                      {template.nome}
                    </option>
                  ))}
                </select>
              </div>


            </div>



            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrizione
              </label>
              <textarea
                value={formData.descrizione}
                onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                rows={2}
                className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Inserisci una descrizione dettagliata"
              />
            </div>
          </div>

          <OfferPromotionsPanel
            promotions={selectablePromozioni}
            selectedIds={formData.promozione_ids || []}
            automaticPromotions={items.flatMap((_, index) => itemPricing[index]?.promozioni_applicate || [])}
            onChange={(promozione_ids) => setFormData((current) => ({ ...current, promozione_ids }))}
            disabled={isEditing && offer?.stato !== 'bozza'}
            loading={promotionsLoading}
            error={promotionsError}
            onRetry={() => refetchPromotions()}
          />

          {/* Validity (totals live in the floating bar; IVA is per-item) */}
          <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="text-base font-medium text-gray-900 mb-3">Validità</h2>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Giorni di Validità
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.giorni_validita}
                  onChange={(e) => setFormData({ ...formData, giorni_validita: parseInt(e.target.value) || 30 })}
                  className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data di Scadenza
                </label>
                <input
                  type="date"
                  value={formData.scadenza}
                  onChange={(e) => setFormData({ ...formData, scadenza: e.target.value })}
                  className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Additional Settings */}
          <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="text-base font-medium text-gray-900 mb-3">Impostazioni Aggiuntive</h2>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Condizioni di Pagamento
                </label>
                <textarea
                  value={formData.condizioni_pagamento}
                  onChange={(e) => setFormData({ ...formData, condizioni_pagamento: e.target.value })}
                  rows={2}
                  className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Inserisci le condizioni di pagamento"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  rows={2}
                  className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Inserisci note aggiuntive"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="firma_digitale"
                  checked={formData.firma_digitale}
                  onChange={(e) => setFormData({ ...formData, firma_digitale: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="firma_digitale" className="ml-2 block text-sm text-gray-900">
                  Richiedi firma digitale
                </label>
              </div>
            </div>
          </div>
          </div>
          </div>
            <button type="button" aria-expanded={configurationOpen} aria-controls="offer-configuration-panel"
              aria-label={configurationOpen ? 'Comprimi configurazione' : 'Espandi configurazione'}
              onClick={() => setConfigurationOpen(!configurationOpen)}
              className="flex w-10 shrink-0 flex-col items-center gap-3 self-stretch border-l border-gray-200 bg-gray-50 py-3 text-gray-600 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500">
              {configurationOpen ? <ChevronRightIcon className="h-4 w-4" /> : <ChevronLeftIcon className="h-4 w-4" />}
              <span className="text-xs tracking-wide [writing-mode:vertical-rl]">Configurazione offerta</span>
            </button>
          </div>
        </form>
      )}

      {/* Content Tab - Editor with Mode Toggle */}
      {activeTab === 'content' && (
        <div className="flex h-[60dvh] min-h-0 max-h-[48rem] flex-col overflow-hidden">
          {offer?.document_config && canExportOffer && <div className="flex gap-3 border-b p-3">
            <button type="button" disabled={hasUnsavedChanges || isSaving} onClick={handlePreviewPDF} className="rounded border px-3 py-2">Anteprima PDF</button>
            <button type="button" disabled={hasUnsavedChanges || isSaving} onClick={handleExportPDF} className="rounded border px-3 py-2">Esporta PDF</button>
          </div>}
          {/* Editor Mode Toggle */}
          <div className={offer?.document_config ? "hidden" : "bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center justify-between"}>
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">Modalità Editor:</span>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setEditorMode('html')}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    editorMode === 'html'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  HTML (WYSIWYG)
                </button>
                <button
                  type="button"
                  onClick={() => setEditorMode('markdown')}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    editorMode === 'markdown'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Markdown
                </button>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              {editorMode === 'html' ? (
                <span>✨ Editor visuale con formattazione diretta</span>
              ) : (
                <span>📝 Editor markdown con sintassi testuale</span>
              )}
            </div>
          </div>

          {/* Editor Content */}
          <div className="min-h-0 flex-1 overflow-auto overscroll-contain">
            {offer?.document_config?.sezioni?.length ? (
              <OfferSections sections={offer.document_config.sezioni} values={formData.section_values || {}} onChange={section_values => setFormData(prev => ({...prev, section_values}))} readOnly={offer.stato !== 'bozza' || !hasPermission('offerte:edit')} />
            ) : editorMode === 'html' ? (
              <HTMLEditorContainer
                initialContent={formData.html_content || ''}
                offertaId={id}
                onSave={handleHTMLSave}
                onExportPDF={isEditing && canExportOffer ? handleExportPDF : undefined}
                onSendForSignature={isEditing && formData.firma_digitale && canSignOffer ? handleSendForSignature : undefined}
                readOnly={!!offer && offer.stato !== 'bozza'}
              />
            ) : (
              <MarkdownEditorContainer
                initialContent={formData.markdown_content || DEFAULT_MARKDOWN_TEMPLATE}
                offertaId={id}
                offerContext={offerContext}
                items={items.map(item => ({
                  descrizione: item.descrizione,
                  quantita: item.quantita,
                  prezzo_unitario: item.prezzo_unitario,
                  sconto_percentuale: item.sconto_percentuale,
                  importo_totale: item.importo_totale || 0,
                }))}
                onSave={handleMarkdownSave}
                onExportPDF={isEditing && canExportOffer ? handleExportPDF : undefined}
                onPreviewPDF={isEditing && canExportOffer ? handlePreviewPDF : undefined}
                onSendForSignature={isEditing && formData.firma_digitale && canSignOffer ? handleSendForSignature : undefined}
                readOnly={!!offer && offer.stato !== 'bozza'}
              />
            )}
          </div>
        </div>
      )}

      {/* Attività Tab - solo in modifica (offerta salvata) */}
      {activeTab === 'attivita' && (
        isEditing && id ? (
          <AttivitaTab context={{ kind: 'offerta', id }} />
        ) : (
          <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
            <CalendarDaysIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Salva prima l'offerta per poter registrare delle attività.</p>
          </div>
        )
      )}

      {/* Pannello fluttuante: riepilogo totali sempre visibile + salvataggio */}
      <div className="sticky bottom-4 z-30 flex justify-center pointer-events-none">
        <div className="pointer-events-auto flex items-center gap-3 sm:gap-5 rounded-2xl border border-gray-200 bg-white/95 backdrop-blur-sm px-4 sm:px-5 py-2.5 shadow-[0_8px_30px_rgba(15,23,42,0.12)]">
          <div className="hidden md:flex flex-col items-center min-w-[56px]">
            <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400">Articoli</span>
            <span className="text-sm font-semibold text-gray-900 tabular-nums">{items.length}</span>
          </div>
          <div className="hidden md:block h-8 w-px bg-gray-200" />
          <div className="flex flex-col items-center min-w-[84px]">
            <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400">Imponibile</span>
            <span className="text-sm font-semibold text-gray-900 tabular-nums">€{formData.importo_imponibile.toFixed(2)}</span>
          </div>
          <div className="hidden sm:flex flex-col items-center min-w-[84px]">
            <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400">IVA</span>
            <span className="text-sm font-semibold text-gray-900 tabular-nums">€{formData.importo_iva.toFixed(2)}</span>
          </div>
          <div className="h-8 w-px bg-gray-200" />
          <div className="flex flex-col items-center min-w-[96px]">
            <span className="text-[10px] font-medium uppercase tracking-wider text-primary-500">Totale</span>
            <span className="text-lg font-bold text-primary-600 tabular-nums leading-tight">€{formData.importo_totale.toFixed(2)}</span>
          </div>
          {canSave && (
            <button
              type="button"
              onClick={submitOffer}
              disabled={isSaving}
              className="ml-1 inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] transition-transform"
            >
              {isSaving ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                  Salvataggio...
                </>
              ) : (
                isEditing ? 'Aggiorna Offerta' : 'Crea Offerta'
              )}
            </button>
          )}
        </div>
      </div>

      {/* Reject Offer Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => { setShowRejectModal(false); setRejectNote(''); }} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Rifiuta offerta</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motivazione (opzionale)
                </label>
                <textarea
                  value={rejectNote}
                  onChange={(e) => setRejectNote(e.target.value)}
                  rows={3}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  placeholder="Inserisci il motivo del rifiuto..."
                />
              </div>
              <div className="mt-4 flex justify-end gap-3">
                <button
                  onClick={() => { setShowRejectModal(false); setRejectNote(''); }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  Annulla
                </button>
                <button
                  onClick={() => rejectOfferMutation.mutate(rejectNote)}
                  disabled={rejectOfferMutation.isPending}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  {rejectOfferMutation.isPending ? 'Rifiutando...' : 'Rifiuta'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <ConfirmModal
          isOpen={showDeleteModal}
          title="Elimina offerta"
          message={`Sei sicuro di voler eliminare l'offerta "${offer?.numero || ''} - ${offer?.titolo || ''}"? Questa operazione è reversibile (soft delete).`}
          confirmLabel="Elimina"
          onConfirm={() => deleteOfferMutation.mutate()}
          onClose={() => setShowDeleteModal(false)}
          isLoading={deleteOfferMutation.isPending}
          variant="danger"
        />
      )}
    </div>
  );
};

export default OfferEditor;
